import type { ConfigSection } from '@/types/configuration'
import { Button, Card, Group, MantineSpacing, Text, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { IconDeviceFloppy, IconLock } from '@tabler/icons-react'
import { createContext, useContext } from 'react'

/** True when the configuration is shown to someone who cannot change it (staff). */
export const ConfigViewOnlyContext = createContext(false)

/** The dealership configuration section being shown, so its form can title the header. */
export const ConfigSectionContext = createContext<ConfigSection | null>(null)

/** For a form's own buttons, such as add and remove, which a disabled fieldset blocks but does not grey out. */
export function useConfigViewOnly(): boolean {
  return useContext(ConfigViewOnlyContext)
}

interface ConfigFormHeaderProps {
  onReset: () => void
  /** For buttons outside the form. Without it, Save submits the form it sits in. */
  onSave?: () => void
  isSaving: boolean
  /** When given, both buttons stay disabled until the form has changes. */
  isDirty?: boolean
  /** Disables both buttons, for example while the section loads. */
  disabled?: boolean
  mb?: MantineSpacing
}

/**
 * The top of every dealership and group configuration screen: the section's title, then the Reset and
 * Save buttons, or a lock in their place when the user can only view it. Inside a dealership section
 * it draws the whole header row; on the group screen, which has its own title, only the right side.
 */
export function ConfigFormHeader({ onReset, onSave, isSaving, isDirty, disabled, mb }: ConfigFormHeaderProps) {
  const viewOnly = useConfigViewOnly()
  const section = useContext(ConfigSectionContext)
  const blocked = disabled || isDirty === false

  // Someone who can only view gets the lock instead of the buttons.
  const actions = viewOnly ? (
    <Tooltip label="View only. Only super admins can change configuration." withArrow>
      <ThemeIcon variant="light" color="gray" size="lg" aria-label="View only">
        <IconLock size={18} />
      </ThemeIcon>
    </Tooltip>
  ) : (
    <Group gap="xs" wrap="nowrap">
      <Button type="button" variant="subtle" onClick={onReset} disabled={blocked || isSaving}>
        Reset
      </Button>
      <Button
        type={onSave ? 'button' : 'submit'}
        onClick={onSave}
        variant="filled"
        loading={isSaving}
        disabled={blocked}
        leftSection={<IconDeviceFloppy size={16} />}
      >
        Save Configuration
      </Button>
    </Group>
  )

  if (!section) {
    return <Group mb={mb}>{actions}</Group>
  }

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder mb={mb}>
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Group gap="xs" wrap="nowrap">
            <section.icon size={24} stroke={1.5} />
            <Title order={3}>{section.title}</Title>
          </Group>
          <Text size="sm" c="dimmed">
            {section.description}
          </Text>
        </div>
        {actions}
      </Group>
    </Card>
  )
}
